import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    description?: string
    requiresAuth?: boolean
    /** 需创作者已通过认证（creatorStatus === '3'） */
    requiresCreator?: boolean
    /** 需企业已通过认证（enterpriseStatus === '3'） */
    requiresEnterprise?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/HomeView.vue'),
        meta: { title: '首页' },
      },
      {
        path: 'orders',
        name: 'orders',
        component: () => import('@/views/HallView.vue'),
        meta: {
          title: '订单大厅',
          description: '公开浏览订单；关键字 / 12 类 / 预算 / 加急 / NDA 筛选；点击卡片进详情。',
        },
      },
      {
        // /orders/publish         → 新建
        // /orders/publish/:orderNo → 编辑草稿（PublishView 读 route.params.orderNo）
        path: 'orders/publish/:orderNo?',
        name: 'publish-order',
        component: () => import('@/views/PublishView.vue'),
        meta: {
          title: '发布订单',
          requiresAuth: true,
          requiresEnterprise: true,
          description: '企业发布订单（需 enterpriseStatus=3）；保存草稿与提交审核两按钮。',
        },
      },
      {
        // 订单大厅 / 匿名 / 其他用户（myRole=other/none）走这条公开路径
        path: 'orders/:orderNo',
        name: 'order-detail',
        component: () => import('@/views/order/DetailView.vue'),
        meta: {
          title: '订单详情',
          description: '订单详情；按钮矩阵按 myRole × status 渲染（登录后才能操作）；候选人/提交交付/驳回三个 Modal。',
        },
      },
      {
        path: 'courses',
        name: 'courses',
        component: () => import('@/views/CourseListView.vue'),
        meta: { title: '技能课堂', description: '录播课程浏览、筛选和购买入口。' },
      },
      {
        path: 'courses/:id',
        name: 'course-detail',
        component: () => import('@/views/CourseDetailView.vue'),
        meta: { title: '课程详情', description: '展示真实章节、课时、试看和课程权益状态。' },
      },
      {
        path: 'courses/:id/order/confirm',
        name: 'course-order-confirm',
        component: () => import('@/views/CourseOrderConfirmView.vue'),
        meta: {
          title: '确认订单',
          requiresAuth: true,
          description: '确认课程、价格、有效期和购买须知后进入支付。',
        },
      },
      {
        path: 'courses/:id/order/:orderNo/pay',
        name: 'course-payment',
        component: () => import('@/views/CoursePaymentView.vue'),
        meta: {
          title: '微信支付',
          requiresAuth: true,
          description: '微信扫码支付，轮询订单状态并跳转学习页。',
        },
      },
      {
        path: 'courses/:id/order/:orderNo/success',
        name: 'course-payment-success',
        component: () => import('@/views/CoursePaymentSuccessView.vue'),
        meta: {
          title: '支付成功',
          requiresAuth: true,
          description: '课程订单支付结果确认、订单摘要与下一步引导。',
        },
      },
      {
        path: 'courses/:id/learn',
        name: 'classroom',
        component: () => import('@/views/ClassroomView.vue'),
        meta: {
          title: '学习课堂',
          requiresAuth: true,
          description: '录播学习、课时访问和播放进度保存。',
        },
      },
      {
        path: 'courses/:id/learn/:lessonId',
        name: 'classroom-lesson',
        component: () => import('@/views/LearnView.vue'),
        meta: {
          title: '学习课堂',
          requiresAuth: true,
          description: '录播学习、课时访问和播放进度保存。',
        },
      },
      {
        path: 'ai',
        name: 'ai-toolbox',
        component: () => import('@/views/AiToolboxView.vue'),
        meta: { title: 'AI 工具箱', description: '第三方 AI 能力入口，不实现平台内部生成工作台。' },
      },
      {
        path: 'workbench',
        name: 'workbench',
        component: () => import('@/views/WorkbenchView.vue'),
        meta: {
          title: 'AI 工作台',
          requiresAuth: true,
          description: '嵌入第三方协同 AIGC 平台工作台，OAuth 授权后进入图片 / 视频 / 漫剧工作台。',
        },
      },
      {
        path: 'points',
        name: 'points-center',
        component: () => import('@/views/PointsCenterView.vue'),
        meta: {
          title: '积分中心',
          requiresAuth: true,
          description: '积分余额、充值套餐与积分明细。',
        },
      },
      {
        path: 'points/recharge/:orderNo/pay',
        name: 'points-recharge',
        component: () => import('@/views/PointsRechargeView.vue'),
        meta: {
          title: '积分充值',
          requiresAuth: true,
          description: '微信扫码支付积分充值订单，轮询订单状态并回到积分中心。',
        },
      },
      {
        path: 'points/recharge/:orderNo/success',
        name: 'points-recharge-success',
        component: () => import('@/views/PointsRechargeSuccessView.vue'),
        meta: {
          title: '充值成功',
          requiresAuth: true,
          description: '积分充值成功页，展示充值详情并提供后续操作。',
        },
      },
      {
        path: 'me',
        name: 'personal-center',
        component: () => import('@/views/PersonalCenterView.vue'),
        meta: {
          title: '个人中心',
          requiresAuth: true,
          description: '展示用户资料、订单、课程和平台内收益。',
        },
      },
      {
        path: 'me/settings',
        name: 'settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: {
          title: '账户设置',
          requiresAuth: true,
          description: '修改昵称、邮箱、简介和头像。',
        },
      },
      {
        path: 'me/enterprise-orders',
        name: 'enterprise-orders',
        component: () => import('@/views/order/MyClientOrdersView.vue'),
        meta: {
          title: '我的发单',
          requiresAuth: true,
          requiresEnterprise: true,
          description: '企业视角：5 Tab（全部 / 草稿 / 待审核 / 进行中 / 已结算），含草稿管理。',
        },
      },
      {
        // 「我的发单」点进来的详情：复用 DetailView，NavBar 自动高亮「我的发单」
        path: 'me/enterprise-orders/:orderNo',
        name: 'enterprise-order-detail',
        component: () => import('@/views/order/DetailView.vue'),
        meta: {
          title: '订单详情',
          requiresAuth: true,
          requiresEnterprise: true,
          description: '从我的发单进入的订单详情；与订单大厅共享 DetailView，按 myRole × status 渲染按钮矩阵。',
        },
      },
      {
        path: 'me/creator-orders',
        name: 'creator-orders',
        component: () => import('@/views/order/MyCreatorOrdersView.vue'),
        meta: {
          title: '我的工单',
          requiresAuth: true,
          requiresCreator: true,
          description: '创作者视角：7 Tab（全部 / 待接单 / 进行中 / 待验收 / 已验收 / 申请未通过 / 已取消）。',
        },
      },
      {
        // 「我的工单」点进来的详情：复用 DetailView，NavBar 自动高亮「我的工单」
        path: 'me/creator-orders/:orderNo',
        name: 'creator-order-detail',
        component: () => import('@/views/order/DetailView.vue'),
        meta: {
          title: '订单详情',
          requiresAuth: true,
          requiresCreator: true,
          description: '从我的工单进入的订单详情；与订单大厅共享 DetailView，按 myRole × status 渲染按钮矩阵。',
        },
      },
      {
        path: 'me/messages',
        name: 'messages',
        component: () => import('@/views/MessagesView.vue'),
        meta: {
          title: '消息中心',
          requiresAuth: true,
          description: '订单动态、系统通知、互动消息、认证 / 课程 / 收益通知统一入口。',
        },
      },
      {
        path: 'apply/creator',
        name: 'apply-creator',
        component: () => import('@/views/ApplyCreatorView.vue'),
        meta: {
          title: '创作者入驻',
          requiresAuth: true,
          requiresCreator: true,
          description: '身份标签、营业执照条件上传和作品申请。',
        },
      },
      {
        path: 'apply/enterprise',
        name: 'apply-enterprise',
        component: () => import('@/views/ApplyEnterpriseView.vue'),
        meta: {
          title: '企业认证',
          requiresAuth: true,
          requiresEnterprise: true,
          description: '企业资质、联系人和营业执照审核。',
        },
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { title: '登录' },
  },
  {
    path: '/legal/:kind',
    name: 'legal',
    component: () => import('@/views/LegalView.vue'),
    props: true,
    meta: { title: '法律文档' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: '页面不存在' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (!auth.initialized) await auth.restore()

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return {
      name: 'login',
      query: { redirect: to.fullPath },
    }
  }

  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'home' }
  }

  if (
    to.meta.requiresCreator &&
    to.name !== 'apply-creator' &&
    auth.user?.creatorStatus !== '3'
  ) {
    return { name: 'apply-creator', query: { reason: 'identity' } }
  }

  if (
    to.meta.requiresEnterprise &&
    to.name !== 'apply-enterprise' &&
    auth.user?.enterpriseStatus !== '3'
  ) {
    return { name: 'apply-enterprise', query: { reason: 'identity' } }
  }

  // status='2'（审核中）/'3'（已通过）用户访问 /apply/creator 或 /apply/enterprise
  // 不再推 /me —— 留在原页面只读查看（顶部 ApplyStatusBanner 已展示状态机）。
  // 避免用户反复跳转、看不到自己提交的内容。

  return true
})

export default router
